import { forwardRef } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { Button, ButtonProps, SpinningLoader, useTooltip } from '@frontend/design-system';

const styles = {
  button: css`
    text-transform: none;
    background: var(--fabric-color-neutral-00);
    min-width: 160px;
    width: max(calc(34% - var(--fabric-number-n-4-rem)), 160px);

    position: relative;

    span {
      flex-direction: column;
    }
  `,
  buttonExpanded: css`
    min-height: 108px;
    padding: var(--fabric-number-n-4-rem);

    & > span {
      gap: var(--fabric-number-n-2-rem);
    }
  `,
  buttonHover: css`
    &:hover {
      background: var(--fabric-color-neutral-5);
      border: 1px solid var(--fabric-color-neutral-60);
    }
  `,
  chip: css`
    position: absolute;
    top: 0;
    right: var(--fabric-number-n-2-rem);
    transform: translateY(-50%);
    line-height: 1;
  `,
  loadingWrapper: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  spinner: css`
    opacity: 0.5;
  `,
};

export type PaymentMethodButtonProps = Omit<ButtonProps, 'variant'> & {
  chip?: React.ReactNode;
  expanded?: boolean;
  tooltip?: string;
};
export const PaymentMethodButton = forwardRef(
  ({ onClick, children, chip, expanded, loading, tooltip, disabled, ...rest }: PaymentMethodButtonProps, ref) => {
    const { triggerProps, tooltipProps, Tooltip } = useTooltip();

    const button = (
      <Button
        variant='secondary'
        css={[styles.button, expanded && styles.buttonExpanded, !disabled && styles.buttonHover]}
        onClick={onClick}
        disabled={disabled}
        {...rest}
        {...(!!tooltip ? triggerProps : {})}
        ref={composeRefs(ref, triggerProps.ref) as React.Ref<HTMLButtonElement>}
      >
        {children}
        {loading && (
          <div css={styles.loadingWrapper}>
            <SpinningLoader size='xs' css={styles.spinner} />
          </div>
        )}
        {chip && <div css={styles.chip}>{chip}</div>}
      </Button>
    );

    if (tooltip) {
      return (
        <>
          {button}
          <Tooltip {...tooltipProps}>{tooltip}</Tooltip>
        </>
      );
    }

    return button;
  }
);

PaymentMethodButton.displayName = 'PaymentMethodButton';
